import React, { useEffect } from 'react'

import {
  Flex,
  Box,
  Text,
  Card,
  Accordion,
  TextProps,
  Link,
} from '@clear/design-system'
import PageContent from '@components/layout/PageContent'
import { HeadTitle } from '@components/layout/HeadTitle'
import { TSADecisionTimeframe } from '@hooks/useLookupData'
import { isFamDiscountExpired, stringifyPrice } from '@utils/pricing'
import { useRouter } from 'next/router'
import { usePricingConfigContext } from '@contexts/PricingContext'
import { useFlag } from '@hooks/useFlag'

const Section: React.FC<{ id?: string }> = ({ children, ...props }) => (
  <Box my={3} {...props}>
    {children}
  </Box>
)

const Question: React.FC = ({ children }) => (
  <Text as="h3" fontFamily="Inter-SemiBold">
    {children}
  </Text>
)

const P: React.FC<TextProps> = ({ children, ...props }) => (
  <Text className="break-words" as="p" my={4} {...props} variant="regular">
    {children}
  </Text>
)
const UL: React.FC = ({ children, ...props }) => (
  <Box as="ul" {...props} sx={{ listStyle: 'inside', my: 4 }}>
    {children}
  </Box>
)

const KtnLink = () => (
  <Link variant="regular" color="brandDarkBlue" href="/ktn-lookup">
    tsaprecheckbyclear.tsa.dhs.gov/ktn-lookup
  </Link>
)

const StatusLink = () => (
  <Link variant="regular" color="brandDarkBlue" href="/status">
    tsaprecheckbyclear.tsa.dhs.gov/status
  </Link>
)
const PhoneLink = () => (
  <Link variant="regular" color="brandDarkBlue" href="tel:1-888-831-9070">
    {' '}
    (1-888-831-9070)
  </Link>
)

const EmailLink = () => (
  <Link
    variant="regular"
    color="brandDarkBlue"
    href="mailto:clearcustomersupport@tsa.dhs.gov"
  >
    {' '}
    email
  </Link>
)

const EmailLinkWithText = () => (
  <Link
    variant="regular"
    color="brandDarkBlue"
    href="mailto:clearcustomersupport@tsa.dhs.gov"
  >
    {' '}
    CLEARCustomerSupport@tsa.dhs.gov
  </Link>
)

const TsaFaqLink = () => (
  <Link
    variant="regular"
    color="brandDarkBlue"
    href="https://www.tsa.gov/precheck/faq"
    target="_blank"
  >
    tsa.gov/precheck/faq
  </Link>
)

const HelpCenterPage = () => {
  const router = useRouter()
  const { pricingConfig } = usePricingConfigContext()
  const enrollmentPrice = pricingConfig?.enroll.currentPrice
  const renewalPrice = pricingConfig?.renew.currentPrice
  const precheckPerYearPrice = ` $${Math.ceil(enrollmentPrice / 5)} `
  const youngAdultFlag = useFlag('youngAdultDiscount')

  useEffect(() => {
    const scrollToElement = () => {
      const docs = document.getElementById('required-documents')
      const militaryDiscount = document.getElementById('military-discount')
      const militaryRenewal = document.getElementById('military-renewal')
      const familyDiscount = document.getElementById('family-discount')
      if (docs) {
        docs.scrollIntoView()
      }
      if (militaryDiscount) {
        militaryDiscount.scrollIntoView()
      }
      if (militaryRenewal) {
        militaryRenewal.scrollIntoView()
      }
      if (familyDiscount) {
        familyDiscount.scrollIntoView()
      }
    }
    if (
      router.asPath.endsWith('#required-documents') ||
      router.asPath.endsWith('#military-discount') ||
      router.asPath.endsWith('#military-renewal') ||
      router.asPath.endsWith('#family-discount')
    ) {
      setTimeout(scrollToElement, 100)
    }
  }, [router.asPath])

  return (
    <Flex flexDirection="column">
      <HeadTitle
        title="Help Center | TSA&nbsp;PreCheck® Enrollment Provided by CLEAR"
        description="Have questions about the TSA&nbsp;PreCheck® Enrollment Provided by CLEAR? Browse our FAQs or call our service and support center."
      />
      <PageContent>
        <Text
          as="h1"
          variant="display"
          maxWidth="40rem"
          fontSize={[6, 7, 8]}
          my={[2, 5]}
          mb={[6, 10]}
          color="brandDarkBlue"
          textAlign="center"
          mx="auto"
        >
          Help Center
        </Text>
        <Card p={10}>
          <Accordion
            title="Application"
            defaultExpanded={router.asPath.includes('#required-documents')}
          >
            <Section>
              <Question>
                How do I enroll in TSA&nbsp;PreCheck® through CLEAR?
              </Question>
              <P>
                TSA&nbsp;PreCheck® enrollment provided by CLEAR makes it easy to
                sign up for the TSA&nbsp;PreCheck® Application Program.{' '}
                <Link variant="regular" color="brandDarkBlue" href="/">
                  Get started online
                </Link>{' '}
                by selecting your plan and following instructions. Complete your
                enrollment at the nearest participating CLEAR location. No
                appointment necessary!
              </P>
            </Section>
            <Section>
              <Question>
                Do I need to make an appointment to enroll in TSA&nbsp;PreCheck®
                through CLEAR?
              </Question>
              <P>
                While appointments are not required, they are available at
                select TSA&nbsp;PreCheck® enrollment locations for your
                convenience. To get started, pre-enroll online and choose the
                enrollment location that works best for you. If appointments are
                offered at your chosen site, you'll have the opportunity to
                select a date and time that fits your schedule. Remember to
                bring all necessary documentation.
              </P>
            </Section>
            <Section>
              <Question>
                How do I reschedule or cancel my appointment to enroll in
                TSA&nbsp;PreCheck® through CLEAR?
              </Question>
              <P>
                If you need to make changes to your appointment, please review
                the modify/cancel link in your appointment reminder
                notification. If you are still unable to make changes, contact
                our customer support by phone at <PhoneLink /> from Monday -
                Friday 8am - 10pm ET, or by emailing team at{' '}
                <EmailLinkWithText />.
              </P>
            </Section>
            <Section id="required-documents">
              <Question>
                What documents are required to enroll for TSA&nbsp;PreCheck®?
              </Question>
              <P>
                For TSA&nbsp;PreCheck®, you must provide an original or
                certified copy* of the following documentation to meet identity
                and citizenship requirements - see below:{' '}
              </P>
              <P fontFamily="Inter-SemiBold">
                List A - ONE document is acceptable from the following forms of
                identification:
              </P>
              <UL>
                <li>
                  Unexpired U.S. Passport (book or card) - demonstrates U.S.
                  Citizenship
                </li>
                <li>
                  Unexpired Enhanced Tribal Card (ETC) - demonstrates U.S.
                  Citizenship
                </li>
                <li>
                  Unexpired Free and Secure Trade (FAST) Card - designates U.S.
                  Citizenship if indicated on the FAST card
                </li>
                <li>
                  Unexpired U.S. Enhanced Driver's License (EDL) or Unexpired
                  Enhanced Identification Card (EID) - designates U.S.
                  Citizenship if indicated on the EDL or EIC
                </li>
                <li>
                  Permanent Resident Card (I-551) often referred to as a “Green
                  Card” - demonstrates Lawful Permanent Resident (LPR) status
                </li>
                <li>
                  Alien Registration Cards and Resident Alien cards are not
                  accepted.
                </li>
                <li>
                  Unexpired Foreign Passport AND immigrant visa with I-551
                  annotation of "Upon Endorsement Serves as Temporary I-551
                  Evidencing Permanent Residence of 1 Year" - demonstrates LPR
                  status
                </li>
                <li>
                  Unexpired Re-entry Permit (I-327) - demonstrates LPR status
                </li>
              </UL>
              <P fontFamily="Inter-SemiBold">
                CLEAR is temporarily unable to accept these documents:
              </P>
              <UL>
                <li>
                  Unexpired Enhanced Tribal Card (ETC) - demonstrates U.S.
                  Citizenship
                </li>
                <li>
                  Unexpired Re-entry Permit (I-327) - demonstrates LPR status
                </li>
              </UL>
              <P>
                A passport cannot be used as a List A document if it is
                expired**. If it has expired within the past 12 months, it can
                be used, but only as a proof of citizenship (List B).
              </P>
              <P fontFamily="Inter-SemiBold">
                List B - At least two documents are required if you do not have
                a document from List A to present. The documents must include
                1.) A valid photo ID, and 2.) A document that meets citizenship
                requirements (i.e., one document from each column below). Please
                note, the name on both required documents must match. If you
                have legally changed your name, you must provide an original or
                certified name change document (such as marriage certificates
                and divorce decrees) in addition to the required documentation.
              </P>
              <P fontFamily="Inter-SemiBold">
                Beginning May 1, 2025, individuals who present non-REAL ID
                driver's license or state IDs for in-person enrollment or
                renewal will be unable to proceed with their appointment.
              </P>
              <P fontFamily="Inter-SemiBold">Valid Photo ID</P>
              <UL>
                <li>
                  Unexpired,{' '}
                  <span className="font-bold">REAL ID compliant</span> driver's
                  license issued by a State or outlying possession of the U.S.
                </li>
                <li>
                  Unexpired,{' '}
                  <span className="font-bold">REAL ID compliant</span> state
                  photo identity card issued by a State or outlying possession
                  of the U.S.
                </li>
                <li>Unexpired U.S. military ID card</li>
                <li>Unexpired U.S. retired military ID card</li>
                <li>Unexpired U.S. military dependent’s card </li>
                <li>Native American tribal document with photo</li>
                <li>
                  Unexpired Department of Homeland Security (DHS)/{' '}
                  Transportation Security Administration (TSA)- Transportation
                  Worker Identification Credential (TWIC)
                </li>
                <li>Unexpired Merchant Mariner Credential (MMC)</li>
              </UL>
              <P fontFamily="Inter-SemiBold">Valid Proof of Citizenship</P>
              <UL>
                <li>U.S. Birth Certificate***</li>
                <li>U.S. Certificate of Citizenship (N-560 or N-561) </li>
                <li>U.S. Certificate of Naturalization (N-550 or N-570) </li>
                <li>U.S. Citizen Identification Card (I-179 or I-197)</li>
                <li>Consular Report of Birth Abroad (FS-240)</li>
                <li>
                  Certification of Report of Birth Abroad (DS-1350 or FS-545)
                </li>
                <li>Expired U.S. passport within 12 months of expiration**</li>
              </UL>
              <P fontFamily="Inter-SemiBold">
                CLEAR is temporarily unable to accept these documents:{' '}
              </P>
              <UL>
                <li>
                  Oklahoma State ID and Driver's Licenses issued after
                  02/17/2026
                </li>
                <li>
                  Nebraska State ID and Driver's Licenses issued after
                  03/01/2026
                </li>
              </UL>
              <P fontStyle="italic">
                *An original or certified copy of a document must be issued by a
                Federal, State, Tribal, county, municipal authority, or outlying
                possession bearing an official seal. Short form or abstract
                birth certificates and notarized copies of documents are not
                acceptable.
              </P>
              <P fontStyle="italic">
                **A passport cannot be used as a List A document if it is
                expired. If it has expired within the past 12 months, it can be
                used, but only as a proof of citizenship (List B). Additionally,
                an expired U.S. passport may not be presented by itself. It must
                be presented with at least one other List B document.
              </P>
              <P fontStyle="italic">
                ***TSA will accept only the following birth records:
              </P>
              <UL>
                <li style={{ fontStyle: 'italic' }}>
                  A long-form U.S. state/territory-issued certified/sealed Birth
                  Certificate; or
                </li>
                <li style={{ fontStyle: 'italic' }}>
                  A U.S. state/territory-issued certified/sealed Birth Abstract
                  that meets the requirements of a Birth Certificate (Please
                  note that Birth Registration/Certificate of Birth Registration
                  records are not acceptable); or
                </li>
                <li style={{ fontStyle: 'italic' }}>
                  A birth certification card that meets the requirements of a
                  Birth Certificate or abstract (see below) (Birth Registration
                  or Notification cards are not acceptable)
                </li>
                <li style={{ fontStyle: 'italic' }}>
                  Also accepted: Birth Certificate from Panama Canal Zone before
                  10/01/1979, or Commonwealth of the Northern Mariana Islands
                  after 01/08/1978, or the Philippines before 07/04/1946.
                </li>
              </UL>
              <P fontStyle="italic">
                Document Requirements for Birth Certificates/Birth Certification
                Cards/Certified Abstracts of Birth
              </P>
              <UL>
                <li style={{ fontStyle: 'italic' }}>
                  Issued by the city, county, or state of birth
                </li>
                <li style={{ fontStyle: 'italic' }}>
                  Lists applicant's full name, date of birth, and place of birth
                </li>
                <li style={{ fontStyle: 'italic' }}>
                  List parent(s) full names.
                </li>
                <ul style={{ listStyleType: 'circle' }}>
                  <li style={{ fontStyle: 'italic', marginLeft: '50px' }}>
                    <span>
                      A full name consists of a first and last name; middle name
                      is not required. "Unknown" entries for one or both parents
                      on a valid birth certificate are acceptable, as long as
                      the parent names fields are present. Additionally, a blank
                      space or asterisks (****) in the space where a mother's
                      and/or father's name would be listed will be deemed
                      acceptable, since there is no standard input across
                      agencies to indicate that one or both parents are unknown.
                    </span>
                  </li>
                </ul>
                <li style={{ fontStyle: 'italic' }}>
                  Has the signature of the city, county, or state official or
                  registrar.
                </li>
                <li style={{ fontStyle: 'italic' }}>
                  Has the date filed with registrar's office.
                </li>
                <li style={{ fontStyle: 'italic' }}>
                  Has the seal of issuing authority (seals may appear, but are
                  not required to be printed, as an embossed or raised overt
                  security feature).
                </li>
              </UL>
              <P fontStyle="italic">
                TSA will NOT accept Birth "Registration" or "Notification"
                Cards, or Birth Registration/Certificate of Birth Registration
                records. Cases in which the sole proof of U.S. birth/citizenship
                is a birth registration record are Invalid Enrollments.
              </P>
              <P fontStyle="italic">Unacceptable U.S. Birth Documents</P>
              <li style={{ fontStyle: 'italic' }}>
                Document illegible due to wear/tear or damaged
              </li>
              <li style={{ fontStyle: 'italic' }}>
                Private hospital-issued document
              </li>
              <li style={{ fontStyle: 'italic' }}>
                Any birth document titled with the word "Registration" or
                "Notification"
              </li>
              <li style={{ fontStyle: 'italic' }}>
                Foreign Birth document that is not an acceptable State
                Department record of birth abroad
              </li>
              <li style={{ fontStyle: 'italic' }}>
                State-issued document stating applicant was born in a foreign
                country
              </li>
              <li style={{ fontStyle: 'italic' }}>
                Puerto Rican Birth Certificates issued before July 1, 2010
              </li>
              <li style={{ fontStyle: 'italic' }}>
                "No Record Certification of Birth" document
              </li>
              <li style={{ fontStyle: 'italic' }}>
                Notarized copies of documents and digital identity documents are
                not acceptable
              </li>
            </Section>
            <Section>
              <Question>
                How long will it take for my Known Traveler Number (KTN) to
                arrive so I can use the TSA&nbsp;PreCheck® lanes?
              </Question>
              <P>
                Most TSA&nbsp;PreCheck® applicants receive their KTN in 3-5
                days, though some applications can take up to{' '}
                {TSADecisionTimeframe}. TSA&nbsp;PreCheck® applicants will
                receive notice of their approval status and KTN by email or
                phone. You can also check your status on our website at{' '}
                <StatusLink />. To retrieve your KTN: Please visit <KtnLink />.
                For customer service, please contact our customer support by
                phone at <PhoneLink /> from Monday - Friday 8am - 10pm ET, or by
                emailing team at <EmailLinkWithText />.
              </P>
            </Section>
            <Section>
              <Question>
                Can I complete one application for both CLEAR+ and
                TSA&nbsp;PreCheck®?
              </Question>
              <P>
                Separate applications are required; however, you can complete
                both at participating CLEAR locations. When you sign up for
                CLEAR+, you have the option to bundle TSA&nbsp;PreCheck®
                enrollment with your CLEAR+ Membership to receive a pricing
                incentive and experience the best of both worlds.
              </P>
            </Section>
            <Section>
              <Question>
                Am I automatically enrolled in CLEAR+ if I apply for
                TSA&nbsp;PreCheck®?
              </Question>
              <P>
                No, enrolling in one program does not grant automatic membership
                in the other.
              </P>
            </Section>
            <Section>
              <Question>
                How should I fill out my name on my application?
              </Question>
              <P>
                The name on your application must be an exact match to the name
                on the identification and proof of citizenship/immigration
                documents you provide at enrollment. The name provided must be
                the name used when making your airline travel reservations.
              </P>
            </Section>
            <Section>
              <Question>
                I think I may have fraudulently enrolled in TSA&nbsp;PreCheck®,
                what do I do? Can I get a refund?
              </Question>
              <P>
                If you or someone you know believes they have fallen victim to a
                scam, report it to your local police department and file a
                report at the Federal Trade Commission's website,{' '}
                <Link
                  variant="regular"
                  color="brandDarkBlue"
                  href="https://reportfraud.ftc.gov/"
                >
                  ReportFraud.ftc.gov
                </Link>
                . Additionally, contact your bank or credit card company
                immediately and inform them of the fraudulent charge. Please
                note, neither TSA nor CLEAR will issue a reimbursement to
                applicants who attempt to enroll in TSA&nbsp;PreCheck® through a
                fraudulent website.
              </P>
            </Section>
          </Accordion>
          <Accordion
            title="Renewal"
            defaultExpanded={router.asPath.includes('#military-renewal')}
          >
            <Question>
              I originally enrolled in TSA&nbsp;PreCheck® with another company.
              Can I renew with CLEAR?
            </Question>
            <P>
              Yes! Regardless of your original enrollment provider, you can
              renew through CLEAR. You may begin the renewal process as early as
              6 months prior to expiration.
            </P>
            <P>
              If you have TSA&nbsp;PreCheck® through Global Entry, NEXUS, or
              SENTRI, visit{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://ttp.cbp.dhs.gov"
              >
                https://ttp.cbp.dhs.gov
              </Link>{' '}
              or call 1-877-227-5511 to renew or find information about your
              membership. These programs are not managed through CLEAR.
            </P>
            <Question>
              Can I renew my TSA&nbsp;PreCheck® membership online?
            </Question>
            <P>
              Yes! Simply select ‘Renew’ on the plan selection page. You will
              need to enter your biographic data or your KTN from your current
              membership to begin the renewal process online at{' '}
              <Link variant="regular" color="brandDarkBlue" href="/">
                tsaprecheckbyclear.tsa.dhs.gov
              </Link>
              . Please note that if you submit multiple TSA&nbsp;PreCheck®
              renewal applications, it could cause a significant delay in
              processing your application. Refunds will not be disbursed when
              multiple applications have been submitted.
            </P>
            <P>
              If your name has changed since enrollment, you may update your
              name by contacting customer support by phone (1-888-831-9070) 8am
              – 10pm ET, weekdays or email (CLEARCustomerSupport@tsa.dhs.gov)
              anytime (up to six months before the expiration date) or visit an
              enrollment location to complete the renewal process in-person.
              Name changes may take up to 45 days to complete and processing
              time varies by individual. You can find CLEAR’s available
              locations{' '}
              <Link variant="regular" color="brandDarkBlue" href="/locations">
                here
              </Link>
              .
            </P>
            <P>
              If you have TSA PreCheck® through Global Entry, NEXUS, or SENTRI,
              you are not eligible to renew through CLEAR. Please visit{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://ttp.cbp.dhs.gov"
              >
                https://ttp.cbp.dhs.gov
              </Link>{' '}
              or call 1-877-227-5511 to renew or find information about your
              membership. These programs are not managed through CLEAR.
            </P>
            <Question>
              Can I renew my CLEAR+ and TSA&nbsp;PreCheck® memberships at the
              same time?
            </Question>

            <P>
              Separate applications are required to renew your CLEAR+ and TSA
              PreCheck® memberships. CLEAR+ is an annual membership while TSA
              PreCheck® is valid for 5 years. To renew your TSA PreCheck
              membership, get started{' '}
              <Link variant="regular" color="brandDarkBlue" href="/">
                here
              </Link>
              .
            </P>

            <Question>How long is my TSA&nbsp;PreCheck® KTN valid?</Question>
            <P>
              Your TSA&nbsp;PreCheck® KTN is valid for 5 years. If your TSA
              PreCheck® membership is approaching expiration, you can renew{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="/renewal/eligibility/eligibility"
              >
                here
              </Link>
              {''}.
            </P>

            <Question>How soon may I renew TSA&nbsp;PreCheck®?</Question>
            <P>
              You may begin the renewal process up to 6 months prior to
              expiration. Applicants will not lose any time for renewing early.
            </P>
            <Section id="military-renewal">
              <Question>
                How can I apply for a Military Survivor Family or military
                spouse discount for my online renewal?
              </Question>
              <P>
                If you are a Military Survivor Family member or a military
                Spouse, email
                <EmailLinkWithText /> to provide your eligible documents (TAPS
                letter or a DoD/Uniformed Services Identification (USID) Card)
                to CLEAR Support via email. Once received, CLEAR Support will
                review and provide a promotional rebate if eligible.
              </P>
              <P>
                TSA PreCheck® customers who renew online and are eligible for
                the military spouse discount or Military Survivor Family free
                offer must submit their rebate request to <EmailLinkWithText />
                within 30 days of their online renewal.
              </P>
              <Question>
                Will the military discount program apply to both enrollments and
                renewals?
              </Question>
              <P>Yes.</P>
              <Question>
                Who is eligible for the discount for military spouses?
              </Question>
              <P>
                All spouses of uniformed service members are eligible for the
                spouse discount. Present your unexpired DoD/USID card at your
                enrollment appointment.
              </P>
              <Question>
                What needs to be listed on the eligible spouse's unexpired
                DoD/Uniformed Services Identification (USID) card?
              </Question>
              <P>
                The relationship will say Spouse and the Sponsor Affiliation
                will say either Active Duty, Reserve or Guard for military and
                uniformed service member's spouses.
              </P>
              <Question>Will expired Military ID cards be accepted?</Question>
              <P>No.</P>
            </Section>
          </Accordion>
          <Accordion title="Benefits">
            <Question>
              Why should I join TSA&nbsp;PreCheck® through CLEAR?{' '}
            </Question>
            <P>
              TSA&nbsp;PreCheck® enrollment provided by CLEAR is easy because
              you can get the process started online and don’t need to make an
              appointment to finish in-person. Simply show up at a convenient
              participating CLEAR location with the correct documents within 180
              days to complete the application. Now you can bundle
              TSA&nbsp;PreCheck® and CLEAR+ for the fastest way to your gate at
              the best value.
            </P>
          </Accordion>
          <Accordion title="General">
            <Question>
              What is the difference between CLEAR+ and TSA&nbsp;PreCheck®? Do I
              need both?
            </Question>
            <P>
              CLEAR+ allows for a quick identity verification experience at the
              airport. Step right up to a CLEAR pod and verify you are you with
              your eyes or fingerprint.
            </P>
            <P>
              TSA&nbsp;PreCheck® is a U.S. government program that identifies
              travelers deemed low-risk by the Transportation Security
              Administration (TSA). Once approved to participate in the program,
              it allows travelers to pass through an expedited security
              screening at{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/precheck/map"
                target="_blank"
              >
                U.S. airports nationwide with no need to remove shoes, laptops,
                3-1-1 liquids, belts or light jackets
              </Link>
              . This allows travelers to enjoy a more convenient and efficient
              experience at airport security.
            </P>
            <P>
              Many of our Members find that these two services complement each
              other — providing the quickest, surest route through the airport.
            </P>

            <Question>
              Are CLEAR and TSA&nbsp;PreCheck® available in the same airports?
            </Question>
            <P>
              CLEAR+ Lanes are available at 50+ airports and TSA&nbsp;PreCheck®
              is available at 200+ locations nationwide. Bundle today for the
              best of both worlds! For additional information for where
              TSA&nbsp;PreCheck® is available, please visit visit{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/precheck/schedule"
                target="_blank"
              >
                https://www.tsa.gov/precheck/schedule
              </Link>
              .
            </P>

            <Question>
              Who can become a CLEAR+ Member? Is it the same for
              TSA&nbsp;PreCheck®?
            </Question>
            <P>Membership qualifications vary for each program.</P>
            <P>
              CLEAR+ welcomes individuals 18 years of age and older. To enroll,
              you must have one of the following forms of photo identification:
              U.S. Driver's License, U.S. Passport, or U.S. State Issued ID. All
              forms of identification must be valid, unexpired, not amended and
              have a photo.
            </P>
            <P>
              TSA PreCheck® membership is open to U.S. citizens, U.S. Nationals
              and Lawful Permanent Residents (LPR). To enroll, you must bring
              documents that meet both identity and citizenship requirements for
              the program. Applicants may be deemed ineligible due to incomplete
              or false application information, certain violations of federal
              security regulations, or{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/Disqualifying-Offenses-Factors"
                target="_blank"
              >
                disqualifying criminal offenses and other elgibility factors.
              </Link>
            </P>

            <Question>
              What is TSA&nbsp;PreCheck® and how does it work?
            </Question>
            <P>
              TSA&nbsp;PreCheck® is a U.S. government program that identifies
              travelers deemed low-risk by the Transportation Security
              Administration (TSA). Once approved to participate in the program,
              it allows travelers to pass through an expedited security
              screening at{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/precheck/map"
                target="_blank"
              >
                U.S. airports nationwide with no need to remove shoes, laptops,
                3-1-1 liquids, belts or light jackets.
              </Link>
            </P>

            <Question>
              Where can I enroll in TSA&nbsp;PreCheck® through CLEAR?
            </Question>
            <P>
              You can enroll at any participating CLEAR airport, flagship and
              Staples{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="/locations"
                target="_blank"
              >
                locations.
              </Link>
            </P>
          </Accordion>
          <Accordion
            title="Costs"
            defaultExpanded={
              router.asPath.includes('#military-discount') ||
              router.asPath.includes('#family-discount')
            }
          >
            {youngAdultFlag?.showPromoItems ? (
              <>
                <Question>How does the Young Adults Discount work?</Question>
                <P>
                  Applicants 30 years old or under can receive $20 off their
                  TSA&nbsp;PreCheck® enrollment when they apply between May 1
                  and May 31, 2026. Eligibility verified via government-issued
                  ID at time of enrollment. The standard TSA&nbsp;PreCheck®
                  enrollment fee is $79.95. With the Young Adults discount
                  applied, the total is $59.95. This promotion is only valid for
                  new enrollments, renewals are not eligible.
                </P>
              </>
            ) : null}
            <Question>
              How much does TSA&nbsp;PreCheck® enrollment provided by CLEAR
              cost?
            </Question>
            <P>
              TSA&nbsp;PreCheck® enrollment provided by CLEAR is only{' '}
              {stringifyPrice(enrollmentPrice)} for 5 years, equating to less
              than {precheckPerYearPrice} a year. Renewal of your membership
              costs {stringifyPrice(renewalPrice)} for online renewals and{' '}
              {stringifyPrice(enrollmentPrice)} for in-person renewals.
            </P>
            <Question>
              Do I save money by bundling TSA&nbsp;PreCheck® and CLEAR+
              together?
            </Question>
            <P>
              When bundling your enrollment or renewal, you enjoy special
              pricing on your first year of CLEAR+ membership for a combined
              total of just $209 (${enrollmentPrice} for TSA&nbsp;PreCheck® and
              $129.05 for CLEAR+; for online renewals you pay ${renewalPrice}{' '}
              for TSA&nbsp;PreCheck® and $139.05 for CLEAR+). In both instances,
              you will be granted TSA PreCheck® for 5 years and CLEAR+
              Membership for 1 year. Upon completion of both applications, you
              will receive a rebate on your CLEAR+ Membership (up to $
              {enrollmentPrice}).
            </P>
            <Question>
              What are the accepted payment methods for the TSA&nbsp;PreCheck®
              application fee?
            </Question>
            <P>
              You can pay with a credit / debit card. CLEAR accepts Mastercard,
              Visa, Discover and American Express.
            </P>
            <Question>
              My personal information has changed. How do I update my
              information so that I can continue to receive TSA&nbsp;PreCheck®?
            </Question>
            <P>
              If you are a member of the TSA&nbsp;PreCheck® Application Program,
              you may call us at <PhoneLink /> 8am - 10pm ET weekdays or
              <EmailLink /> anytime to make changes to your name, address, or
              other information contained in your records. If you change your
              name and do not update your TSA&nbsp;PreCheck® membership to match
              the new details, you will not be able to use your
              TSA&nbsp;PreCheck® benefits when traveling until the name change
              process is completed.
            </P>
            <Question>
              Are there any Senior Citizen discounts for TSA PreCheck®?
            </Question>
            <P>
              TSA PreCheck® is not currently offered at discounted membership
              rates for senior citizens. However, please note that TSA allows
              travelers 75+ to leave shoes and a light jacket on during
              screening. For more information visit{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/travel/special-procedures/screening-passengers-75-and-older"
                target="_blank"
              >
                tsa.gov/travel/special-procedures/screening-passengers-75-and-older
              </Link>
              .
            </P>
            <Section id="military-discount">
              <Question>
                Are there any military discounts for TSA&nbsp;PreCheck®?
              </Question>
              <P>
                Uniformed service members and students at the armed forces'
                service academies automatically enjoy TSA&nbsp;PreCheck®
                benefits, with no need to enroll separately. Their DoD ID number
                is a 10-digit number on the back of the Common Access Card which
                serves as their Known Traveler Number (KTN).
              </P>
              <P>
                Military Survivor Families receive complimentary
                TSA&nbsp;PreCheck® enrollment and renewal with presentation of
                TAPS letter or TSA acknowledgement form.
              </P>
              <P>
                Military spouses receive $25 off of TSA&nbsp;PreCheck®
                enrollment and renewal with presentation of unexpired DoD/USID
                card where spouse is listed as the relationship.
              </P>
              <Question>
                Will the military discount program apply to both enrollments and
                renewals?
              </Question>
              <P>Yes.</P>
              <Question>
                Who is eligible for the discount for military spouses?
              </Question>
              <P>
                All spouses of uniformed service members are eligible for the
                spouse discount. Present your unexpired DoD/USID card at your
                enrollment appointment.
              </P>
              <Question>
                What needs to be listed on the eligible spouse's unexpired
                DoD/Uniformed Services Identification (USID) card?
              </Question>
              <P>
                The relationship will say Spouse and the Sponsor Affiliation
                will say either Active Duty, Reserve or Guard for military and
                uniformed service member's spouses.
              </P>
              <Question>Will expired Military ID cards be accepted?</Question>
              <P>No.</P>
            </Section>
            {isFamDiscountExpired ? null : (
              <Section id="family-discount">
                <Question>How does Family Discounts work?</Question>
                <P>
                  This promotion will be offered to family members in groups of
                  2. If a family larger than two shows up, the Family Discount
                  can still be offered. If an odd number of applications is
                  present (e.g. 3), the Family Discount can only be offered in
                  pairs and the extra enrollment will be full price ($
                  {enrollmentPrice}).
                </P>
              </Section>
            )}
            <Question>
              Do any credit card companies offer discounts for
              TSA&nbsp;PreCheck®?
            </Question>
            <P>
              The following credit cards listed{' '}
              <Link
                variant="regular"
                color="brandDarkBlue"
                href="https://www.tsa.gov/precheck/credit-cards-offer"
                target="_blank"
              >
                here
              </Link>
              , cover the TSA&nbsp;PreCheck® application fee as a member
              benefit. Check to see if your credit card participates.{' '}
            </P>
          </Accordion>
          <Accordion title="Accounts">
            <Question>What is my KTN?</Question>
            <P>
              A KTN or Known Traveler Number, is a unique number issued by the
              TSA indicating you've completed a Security Threat Assessment and
              are eligible for TSA&nbsp;PreCheck® expedited screening. Your KTN
              is valid for 5 years and should be used when booking airline
              reservations. To retrieve your KTN once you are approved, you can
              visit <KtnLink />.
            </P>
            <Question>
              Can I see my TSA&nbsp;PreCheck® registration on my CLEAR app?
            </Question>
            <P>At this time, this is not an available feature.</P>
          </Accordion>
          <Accordion title="Family">
            <Question>
              Can my spouse or partner use my TSA&nbsp;PreCheck® account when
              traveling without me?
            </Question>
            <P>
              No. TSA&nbsp;PreCheck® benefits only apply to those who are
              enrolled and traveling with the TSA&nbsp;PreCheck® indicator on
              their boarding pass.
            </P>
            <Question>
              I’m traveling with a guest / travel partner, can they use the
              TSA&nbsp;PreCheck® lanes?
            </Question>
            <P>
              Travelers 18 and older who do not have a TSA&nbsp;PreCheck®
              indicator on their boarding pass must go through standard security
              lanes. For more information visit <TsaFaqLink />.
            </P>
            <Question>Can children use the TSA&nbsp;PreCheck® lane?</Question>
            <P>
              Children ages 12 and under may use the TSA&nbsp;PreCheck® lane
              when traveling with a parent or guardian who has the
              TSA&nbsp;PreCheck® indicator on their boarding pass. Children
              13-17 may join enrolled adults when traveling on the same
              reservation and if the TSA&nbsp;PreCheck® indicator appears on the
              child's boarding pass. For more information visit <TsaFaqLink />.
            </P>
            <P>
              Please note, adults using gate passes to accompany children
              traveling alone will be directed to standard screening as gate
              passes are excluded from TSA&nbsp;PreCheck® benefits. This applies
              even if the gate pass holder has enrolled in TSA&nbsp;PreCheck®.
              The adult will not be permitted to join the child in the
              TSA&nbsp;PreCheck® lane.
            </P>
            <Question>Can children use the CLEAR+ Lane?</Question>
            <P>
              When accompanied by a CLEAR+ Member, children 17 and under may use
              the standard CLEAR+ Lane. When accompanied by a CLEAR+ Member on
              the same reservation and has the TSA&nbsp;PreCheck® indicator on
              their boarding pass, children ages 17 and under may go through the
              CLEAR+ with TSA&nbsp;PreCheck® Lane.
            </P>
            <Question>
              Do children need to apply for TSA&nbsp;PreCheck®?
            </Question>
            <P>
              It depends on their age and whether they are traveling with a
              parent or guardian who has enrolled in TSA&nbsp;PreCheck®:
              <UL>
                <li>
                  Children 12 and under may accompany an enrolled parent or
                  guardian in the TSA&nbsp;PreCheck® lanes without restriction.
                </li>
                <li>
                  Children 13-17 may join enrolled adults when traveling on the
                  same reservation and if the TSA&nbsp;PreCheck® indicator
                  appears on the child’s boarding pass.
                </li>
                <li>
                  Any child traveling alone routinely may want to enroll in the
                  program.
                </li>
              </UL>
              Please note, adults using gate passes to accompany children
              traveling alone will be directed to standard screening as gate
              passes are excluded from TSA&nbsp;PreCheck® benefits. This applies
              even if the gate pass holder has enrolled in TSA&nbsp;PreCheck®.
              The adult will not be permitted to join the child in the
              TSA&nbsp;PreCheck® lane.
            </P>
          </Accordion>
          <Accordion
            defaultExpanded={router.asPath.includes('#fbi')}
            title="FBI Information"
          >
            <Section id="fbi">
              <Question>
                How would I learn more about FBI Identity History Summary
                Checks?
              </Question>
              <P>
                Please visit{' '}
                <Link
                  variant="regular"
                  color="brandDarkBlue"
                  href="http://www.fbi.gov/checks"
                  target="_blank"
                >
                  Identity History Summary Checks
                </Link>{' '}
                for answers to your questions.
              </P>
              <Question>How do I report a PII breach to the FBI?</Question>
              <P>
                Please see the{' '}
                <Link
                  variant="regular"
                  color="brandDarkBlue"
                  href="https://www.fbi.gov/file-repository/cyber-incident-reporting-united-message-final.pdf/view"
                  target="_blank"
                >
                  Cyber Incident Reporting Brochure
                </Link>{' '}
                for answers to your questions.
              </P>
              <Question>How do I view the FBI Privacy Act Statement?</Question>
              <P>
                Please see the{' '}
                <Link
                  variant="regular"
                  color="brandDarkBlue"
                  href="https://www.fbi.gov/how-we-can-help-you/more-fbi-services-and-information/compact-council/privacy-act-statement"
                  target="_blank"
                >
                  FBI Privacy Act Statement
                </Link>
                .
              </P>
              <Question>
                How do I view the Noncriminal Justice Applicant's Privacy
                Rights?
              </Question>
              <P>
                Please see the{' '}
                <Link
                  variant="regular"
                  color="brandDarkBlue"
                  href="https://www.fbi.gov/how-we-can-help-you/more-fbi-services-and-information/compact-council/guiding-principles-noncriminal-justice-applicants-privacy-rights"
                  target="_blank"
                >
                  Noncriminal Justice Applicant's Privacy Rights
                </Link>{' '}
                for answers to your questions.
              </P>
            </Section>
          </Accordion>
          <Text as="p" mt={9} textAlign="center">
            If you're unable to find your answer and need help,{' '}
            <Text as="a" href="/contact-us" color="brandMidnightBlue">
              contact us
            </Text>
            .
          </Text>
        </Card>
      </PageContent>
    </Flex>
  )
}

export default HelpCenterPage
